import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionDivider } from './shared';

function impactColor(impact) {
  if (!impact) return '#6b8dad';
  if (/bullish/i.test(impact) || impact.includes('🟢')) return '#059669';
  if (/sell|risk/i.test(impact) || impact.includes('🔴')) return '#dc2626';
  return '#d97706';
}
function sellPressureColor(rating) {
  if (!rating) return '#6b8dad';
  if (/low/i.test(rating)  || rating.includes('🟢')) return '#059669';
  if (/high/i.test(rating) || rating.includes('🔴')) return '#dc2626';
  return '#d97706';
}

export default function EventsUnlockCard({ events }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('events');
  const { catalysts, unlocks } = events;
  const spColor = sellPressureColor(unlocks?.sellRating || catalysts?.sellRating);

  return (
    <Card title={t('eventsUnlockCard.title')} icon="📅">
      <div style={s.tabs}>
        {[
          { key: 'events',  label: `🗓 ${t('eventsUnlockCard.tabEvents')}` },
          { key: 'unlocks', label: `🔓 ${t('eventsUnlockCard.tabUnlocks')}` },
        ].map(tabItem => (
          <button key={tabItem.key} style={{ ...s.tab, ...(tab === tabItem.key ? s.tabActive : {}) }} onClick={() => setTab(tabItem.key)}>
            {tabItem.label}
          </button>
        ))}
      </div>

      {tab === 'events' && (
        <div style={s.panel}>
          {catalysts?.events?.length > 0 ? (
            <div style={s.eventList}>
              {catalysts.events.map((ev, i) => {
                const ic = impactColor(ev.impact);
                return (
                  <div key={i} style={s.eventRow}>
                    <div style={{ ...s.impactBar, background: ic }} />
                    <div style={s.eventDate}>{ev.date}</div>
                    <div style={s.eventDesc}>{ev.event}</div>
                    <span style={{ ...s.impactBadge, color: ic, borderColor: ic + '40', background: ic + '12' }}>
                      {ev.impact.replace(/🟢|🟡|🔴/g, '').trim()}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={s.empty}>{t('eventsUnlockCard.noEvents')}</p>
          )}
        </div>
      )}

      {tab === 'unlocks' && (
        <div style={s.panel}>
          {unlocks?.schedule?.length > 0 ? (
            <>
              <div style={s.unlockTable}>
                <div style={s.unlockHeader}>
                  {[t('eventsUnlockCard.colMonth'), t('eventsUnlockCard.colCategory'), 'Token', t('eventsUnlockCard.colPctSupply'), t('eventsUnlockCard.colEstValue')].map(h => (
                    <span key={h} style={s.unlockTh}>{h}</span>
                  ))}
                </div>
                {unlocks.schedule.map((row, i) => (
                  <div key={i} style={{ ...s.unlockRow, background: i % 2 === 0 ? 'var(--surface2)' : 'transparent' }}>
                    <span style={s.unlockCell}>{row.month}</span>
                    <span style={s.unlockCell}>{row.category}</span>
                    <span style={s.unlockCell}>{row.tokens}</span>
                    <span style={{ ...s.unlockCell, color: '#d97706', fontWeight: 700 }}>{row.pct}</span>
                    <span style={s.unlockCell}>{row.value}</span>
                  </div>
                ))}
              </div>
              {Object.keys(unlocks.cumulative || {}).length > 0 && (
                <>
                  <SectionDivider label={t('eventsUnlockCard.cumulativeUnlock')} />
                  <div style={s.cumRow}>
                    {[[t('eventsUnlockCard.months3'), unlocks.cumulative['3m']], [t('eventsUnlockCard.months6'), unlocks.cumulative['6m']], [t('eventsUnlockCard.months12'), unlocks.cumulative['12m']]]
                      .filter(([, v]) => v)
                      .map(([label, val]) => (
                        <div key={label} style={s.cumItem}>
                          <span style={s.cumLabel}>{label}</span>
                          <span style={s.cumValue}>{val}</span>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <p style={s.empty}>{t('eventsUnlockCard.noUnlockData')}</p>
          )}

          {(unlocks?.sellRating || unlocks?.sellReason) && (
            <div style={{ ...s.sellBox, borderColor: spColor + '40', background: spColor + '10' }}>
              <span style={{ ...s.sellLabel, color: spColor }}>
                {t('eventsUnlockCard.sellPressure')}: {unlocks.sellRating?.replace(/🔴|🟡|🟢/g, '').trim()}
              </span>
              {unlocks.sellReason && <span style={s.sellReason}>{unlocks.sellReason}</span>}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

const s = {
  tabs: { display: 'flex', gap: 6, marginBottom: 14 },
  tab: {
    flex: 1, padding: '7px 12px', fontSize: 11, fontWeight: 600,
    background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text-muted)', cursor: 'pointer', transition: 'all 0.15s',
    fontFamily: 'var(--font)',
  },
  tabActive: { background: 'rgba(31,88,164,0.1)', color: '#1f58a4', borderColor: 'rgba(31,88,164,0.3)' },
  panel: { display: 'flex', flexDirection: 'column', gap: 8 },
  eventList: { display: 'flex', flexDirection: 'column', gap: 6 },
  eventRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '9px 12px', overflow: 'hidden', position: 'relative',
  },
  impactBar: { width: 3, height: 36, borderRadius: 2, flexShrink: 0 },
  eventDate: { fontSize: 11, color: 'var(--text-dim)', minWidth: 75, fontWeight: 600, fontFamily: 'monospace' },
  eventDesc: { flex: 1, fontSize: 12, color: 'var(--text-muted)' },
  impactBadge: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5, border: '1px solid', whiteSpace: 'nowrap' },
  empty: { fontSize: 13, color: 'var(--text-dim)', padding: '16px 0' },
  unlockTable: { display: 'flex', flexDirection: 'column', gap: 2 },
  unlockHeader: {
    display: 'grid', gridTemplateColumns: '90px 1fr 80px 80px 100px',
    gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--border)',
  },
  unlockTh: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.7 },
  unlockRow: { display: 'grid', gridTemplateColumns: '90px 1fr 80px 80px 100px', gap: 8, padding: '8px 10px', borderRadius: 6 },
  unlockCell: { fontSize: 11, color: 'var(--text-muted)' },
  cumRow: { display: 'flex', gap: 12 },
  cumItem: { flex: 1, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 3 },
  cumLabel: { fontSize: 10, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7 },
  cumValue: { fontSize: 13, fontWeight: 700, color: '#d97706' },
  sellBox: { border: '1px solid', borderRadius: 8, padding: '10px 14px', marginTop: 8 },
  sellLabel: { fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 },
  sellReason: { fontSize: 11, color: 'var(--text-muted)' },
};
